import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../../redux/store";
import axiosInstance from "../../../api/axiosInstance";
import endpoints from "../../../api/endpoints";

export interface Article {
  Reference: string;
  Description: string;
  Unite: string;
  price?: number;
}

export interface ArticleWithQuantity extends Article {
  quantity?: number;
  commentaire?: string;
  typeArticle?: string;
  id: string;
}

interface ArticleState {
  articles: Article[];
  selectedArticles: ArticleWithQuantity[];
  totalPrice: number;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: ArticleState = {
  articles: [],
  selectedArticles: [],
  totalPrice: 0,
  status: "idle",
  error: null,
};

export const fetchArticles = createAsyncThunk(
  "articles/fetchArticles",
  async (
    {
      searchPattern,
      pageNumber,
    }: { searchPattern: string; pageNumber: number },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;

      const language = state.languageSlice.language;

      const response = await axiosInstance.post(
        endpoints.articles.getArticlesStandard,
        { searchPattern, pageNumber },
        {
          headers: {
            "Accept-Language": language,
          },
        }
      );


      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Network error");
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

const calculateTotalPrice = (selectedArticles: ArticleWithQuantity[]) => {
  return selectedArticles.reduce(
    (total, article) => total + (article.price ?? 0) * (article.quantity ?? 1),
    0
  );
};
const articleStandardSlice = createSlice({
  name: "articles",
  initialState,
  reducers: {
    addSelectedArticle(state, action: PayloadAction<ArticleWithQuantity>) {
      state.selectedArticles.push(action.payload);
      state.totalPrice = calculateTotalPrice(state.selectedArticles);
    },
    updateArticleQuantity(
      state,
      action: PayloadAction<{ id: string; quantity: number }>
    ) {
      const article = state.selectedArticles.find(
        (article) => article.id === action.payload.id
      );
      if (article) {
        article.quantity = action.payload.quantity;
        state.totalPrice = calculateTotalPrice(state.selectedArticles);
      }
    },
    removeSelectedArticle(state, action: PayloadAction<string>) {
      state.selectedArticles = state.selectedArticles.filter(
        (article) => article.id !== action.payload
      );
      state.totalPrice = calculateTotalPrice(state.selectedArticles);
    },
    clearSelectedArticles(state) {
      state.selectedArticles = [];
      state.totalPrice = 0;
    },
    updateArticleCommentaire(
      state,
      action: PayloadAction<{ id: string; commentaire: string }>
    ) {
      const article = state.selectedArticles.find(
        (article) => article.id === action.payload.id
      );
      if (article) {
        article.commentaire = action.payload.commentaire;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArticles.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.articles = action.payload.map((a: any) => ({
          Reference: a.reference,
          Description: a.description,
          Unite: a.unite,
        }));
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

export const {
  addSelectedArticle,
  removeSelectedArticle,
  clearSelectedArticles,
  updateArticleQuantity,
  updateArticleCommentaire,
} = articleStandardSlice.actions;

export default articleStandardSlice.reducer;
